import React from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  useColorModeValue,
  Icon,
  Container,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { FiDroplet, FiShoppingCart, FiGift, FiStar } from "react-icons/fi";

const Hero: React.FC = () => {
  const bgColor = useColorModeValue("white", "gray.800");
  const accentColor = useColorModeValue("purple.500", "purple.300");

  return (
    <Box
      position="relative"
      minH="90vh"
      bg="purple.900"
      overflow="hidden"
      display="flex"
      alignItems="center"
    >
      {/* Luxury Background Pattern */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bgGradient="radial(circle at center, #553C9A 0%, #322659 100%)"
        opacity={0.95}
      />

      {/* Elegant Pattern Overlay */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bgImage="url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMzAgMzBoMjB2MjBIMzB6IiBmaWxsPSJub25lIiBzdHJva2U9IiNBNjZDMkMiIHN0cm9rZS13aWR0aD0iMSIgc3Ryb2tlLW9wYWNpdHk9IjAuMSIvPjwvc3ZnPg==')"
        opacity={0.1}
      />

      {/* Decorative Elements */}
      <Box
        position="absolute"
        top="-20%"
        right="-20%"
        width="60%"
        height="60%"
        bgGradient="radial(circle, purple.400 0%, transparent 70%)"
        opacity={0.1}
        filter="blur(40px)"
        transform="rotate(45deg)"
      />
      <Box
        position="absolute"
        bottom="-20%"
        left="-20%"
        width="60%"
        height="60%"
        bgGradient="radial(circle, purple.400 0%, transparent 70%)"
        opacity={0.1}
        filter="blur(40px)"
        transform="rotate(-45deg)"
      />

      <Container maxW="container.xl" position="relative" zIndex={1}>
        <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={8} alignItems="center">
          {/* Left Content */}
          <GridItem>
            <VStack align="start" spacing={6}>
              <Badge
                colorScheme="purple"
                px={3}
                py={1}
                borderRadius="full"
                fontSize="sm"
                letterSpacing="wider"
              >
                Luxury Fragrances
              </Badge>
              <Text
                fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                fontWeight="bold"
                color="white"
                lineHeight="shorter"
                letterSpacing="tight"
              >
                Discover Your Signature Scent
              </Text>
              <Text
                fontSize={{ base: "lg", md: "xl" }}
                color="purple.100"
                maxW="600px"
              >
                Immerse yourself in our collection of exquisite fragrances, crafted with the finest ingredients from around the world.
              </Text>
              <HStack spacing={4}>
                <Button
                  as={RouterLink}
                  to="/products"
                  size="lg"
                  colorScheme="purple"
                  rightIcon={<FiDroplet />}
                  _hover={{
                    transform: "translateY(-2px)",
                    boxShadow: "lg",
                    transition: "all 0.3s",
                  }}
                >
                  Explore Collection
                </Button>
                <Button
                  as={RouterLink}
                  to="/sale"
                  size="lg"
                  variant="outline"
                  color="white"
                  borderColor="white"
                  _hover={{
                    bg: "whiteAlpha.200",
                    transform: "translateY(-2px)",
                    boxShadow: "lg",
                    transition: "all 0.3s",
                  }}
                >
                  Special Offers
                </Button>
              </HStack>
            </VStack>
          </GridItem>

          {/* Right Content - Stylized Perfume Bottle */}
          <GridItem display={{ base: "none", lg: "block" }}>
            <Box
              position="relative"
              height="600px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Box
                position="relative"
                width="300px"
                height="400px"
                _hover={{
                  transform: "scale(1.05)",
                  transition: "transform 0.5s ease-in-out",
                }}
              >
                {/* Bottle Body */}
                <Box
                  position="absolute"
                  bottom="0"
                  left="50%"
                  transform="translateX(-50%)"
                  width="120px"
                  height="300px"
                  bg="whiteAlpha.200"
                  borderRadius="20px"
                  backdropFilter="blur(10px)"
                  border="2px"
                  borderColor="whiteAlpha.300"
                />
                
                {/* Bottle Neck */}
                <Box
                  position="absolute"
                  bottom="280px"
                  left="50%"
                  transform="translateX(-50%)"
                  width="40px"
                  height="60px"
                  bg="whiteAlpha.200"
                  borderRadius="10px"
                  backdropFilter="blur(10px)"
                  border="2px"
                  borderColor="whiteAlpha.300"
                />
                
                {/* Bottle Cap */}
                <Box
                  position="absolute"
                  bottom="320px"
                  left="50%"
                  transform="translateX(-50%)"
                  width="60px"
                  height="40px"
                  bg="whiteAlpha.300"
                  borderRadius="10px"
                  backdropFilter="blur(10px)"
                  border="2px"
                  borderColor="whiteAlpha.400"
                />
                
                {/* Liquid Effect */}
                <Box
                  position="absolute"
                  bottom="0"
                  left="50%"
                  transform="translateX(-50%)"
                  width="100px"
                  height="200px"
                  bgGradient="linear(to-b, purple.400, purple.600)"
                  opacity="0.3"
                  borderRadius="15px 15px 0 0"
                  backdropFilter="blur(5px)"
                />
                
                {/* Decorative Elements */}
                <Box
                  position="absolute"
                  top="-20px"
                  left="50%"
                  transform="translateX(-50%)"
                  width="100%"
                  height="20px"
                  display="flex"
                  justifyContent="center"
                  gap="10px"
                >
                  {[...Array(3)].map((_, i) => (
                    <Box
                      key={i}
                      width="8px"
                      height="8px"
                      borderRadius="full"
                      bg="whiteAlpha.400"
                      _hover={{
                        bg: "whiteAlpha.600",
                        transform: "scale(1.2)",
                        transition: "all 0.3s",
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </GridItem>
        </Grid>
      </Container>
    </Box>
  );
};

export default Hero;
